import { createContext } from 'react';
import type { LoginRequest, LoginResponse, RegisterRequest, Tenant, User } from '@cyphex/types';
import type { MfaVerifyRequest } from '../api';

/**
 * `checking` is the cold-start state: there may be a refresh cookie, so the app
 * must not flash the sign-in page before the rotation attempt has answered.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  tenant: Tenant | null;
  /**
   * The credential must be replaced before anything else. The API enforces this
   * independently — such a session's token is only accepted by the
   * change-password endpoints — so this flag exists to show the right screen,
   * not to be the control. `passwordChanged()` clears it.
   */
  mustChangePassword: boolean;
  signIn(input: LoginRequest): Promise<void>;
  completeMfa(input: MfaVerifyRequest): Promise<void>;
  signUp(input: RegisterRequest): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Adopt a session this provider did not itself request.
   *
   * WHY IT HAS TO EXIST. A few flows sign somebody in without ever calling
   * `/auth/login`: redeeming an invitation is one, and it hands back the same
   * envelope a login does. Those screens used to call `setAccessToken`
   * directly, which puts a live token in the HTTP layer and leaves this
   * provider still saying `anonymous` — so the very next guarded route bounced
   * the freshly-created account to the sign-in page it had just been spared.
   * There is exactly one owner of session state, and this is how you hand it
   * one.
   *
   * Awaitable because adopting a session also mints the file-content ticket
   * before it reports `authenticated`; callers that navigate on completion
   * should await it so the next screen paints with the cookie already set.
   */
  adoptSession(session: LoginResponse): Promise<void>;
  /** Called once the forced change succeeded, to leave remediation mode. */
  passwordChanged(): void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
